import { useCallback, useEffect } from "react";
import { stringOrDate } from "react-big-calendar";

import { DateTime } from "@bps/date-time";
import { CalendarEventView } from "@shared-types/booking/calendar-event-view.type.ts";

import {
  getWorkWeekViewMax,
  getWorkWeekViewMin
} from "../components/calendar/bookingCalendarWorkWeekUtils.ts";
import { useBookingCalendarScreenContext } from "../context/BookingCalendarScreenContext.tsx";

interface UseScrollToNowProps {
  centerOnCurrentTime?: boolean;
  date: stringOrDate | undefined;
  view: string | undefined;
}

const NOW_POSITION = 0.25; // how far down "now" line will appear.  0.25 = 25% from the top

export const useScrollToNow = ({
  centerOnCurrentTime,
  date,
  view
}: UseScrollToNowProps) => {
  const { providerAndLocationHours } = useBookingCalendarScreenContext();

  const now = DateTime.now();

  const max =
    view !== CalendarEventView.WorkWeek
      ? now.endOf("day")
      : DateTime.fromJSDate(getWorkWeekViewMax(providerAndLocationHours)).set({
          day: now.day,
          month: now.month,
          year: now.year
        });

  const min =
    view !== CalendarEventView.WorkWeek
      ? now.startOf("day")
      : DateTime.fromJSDate(getWorkWeekViewMin(providerAndLocationHours)).set({
          day: now.day,
          month: now.month,
          year: now.year
        });

  // providerAndLocationHours change a lot, so these need to be outside the useCallback
  const maxMillis = max.toMillis();
  const minMillis = min.toMillis();

  const scrollToTime = useCallback(() => {
    if (centerOnCurrentTime) {
      const scrollToDate =
        typeof date === "string"
          ? DateTime.fromISO(date)
          : DateTime.fromJSDate(date);

      const isDayView: boolean = view === CalendarEventView.Day;
      if (
        scrollToDate?.startOf(isDayView ? "day" : "week").toMillis() ===
        DateTime.now()
          .startOf(isDayView ? "day" : "week")
          .toMillis()
      ) {
        const scrollDiv =
          document.getElementsByClassName("rbc-time-content")[0];

        const day = document.getElementsByClassName("rbc-events-container")[0];

        if (!scrollDiv || !day) {
          return;
        }

        const nowRatio =
          (DateTime.now().toMillis() - minMillis) / (maxMillis - minMillis);

        const viewHeight = scrollDiv.clientHeight;
        const dayHeight = day.clientHeight;

        const nowPosition = nowRatio * dayHeight - viewHeight * NOW_POSITION;
        const maxPosition = dayHeight - viewHeight;

        // for work week view, it is possible for the "now" time to be outside the view.  If so, scroll should be at the bottom or top.
        if (nowPosition < 0) {
          scrollDiv.scrollTop = 0;
        } else if (nowPosition > maxPosition) {
          scrollDiv.scrollTop = maxPosition;
        } else {
          scrollDiv.scrollTop = nowPosition;
        }
      }
    }
  }, [centerOnCurrentTime, date, view, maxMillis, minMillis]);

  useEffect(() => {
    scrollToTime();
  }, [date, scrollToTime, view]);
};
